import React, { useCallback, useState, Fragment, useEffect, useRef } from 'react';
import { useTranslate, Button } from 'react-admin';
import { useHistory } from 'react-router-dom';

import { SwapHoriz } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import {
  CircularProgress,
  Container,
  Drawer,
  Grid,
  SvgIcon,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Typography,
} from '@material-ui/core';

import { H2 } from '~/components';

import { ArrowForwardCircleIcon, CloseCircleIcon } from '~/img';
import { createTransaction as createSettlementTransaction } from '~/api/settlement';
import { createTransaction as createAccountTransferTransaction } from '~/api/accountTransfer';
import { createTransaction as createDebitOrderTransaction } from '~/api/debitOrder';
import { getTransaction } from '~/api/headTransactions';
import { HeadTransaction } from '~/types/HeadTransaction';

import SettlementForm from './SettlementForm';
import RunTransferActionResult from './RunTransferActionResult';
import { ConsentHolder } from './RunTransferActionTypes';
import RunTransferActionConsent from './RunTransferActionConsent';
import { Skeleton } from '@material-ui/lab';
import { useNotify } from '~/hooks';
import { OperationType } from '~/types/OperationType';
import AccountTransferForm from './AccountTransferForm';
import DebitOrderForm from './DebitOrderForm';
import OperationTypeInput from '../OperationTypeInput';

export const l = (key: string): string => `components.RunTransferAction.${key}`;

const STATUS_POLL_INTERVAL = 2000;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type RunTransferActionProps = {
  profileId: string;
  resource: 'individuals' | 'businesses' | 'partners' | 'tenant/accounts';
  permissions: string[];
  onDone: () => void;
};

const RunTransferAction = ({
  profileId,
  resource,
  permissions,
  onDone,
}: RunTransferActionProps) => {
  const [open, setOpen] = useState(false);

  const handleClose = () => setOpen(false);

  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));

  const onClick = () => {
    setOpen(true);
  };

  const classes = useStyles();

  const [consent, setConsent] = useState<ConsentHolder | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [operationType, setOperationType] = useState<OperationType>();

  useEffect(() => {
    setConsent(undefined);
    setLoading(false);
    setOperationType(undefined);
  }, [open]);

  const onConsent = useCallback(
    (consent: ConsentHolder | undefined) => {
      setConsent(consent);
    },
    [setConsent]
  );

  const notify = useNotify();
  const history = useHistory();

  const [resultOpen, setResultOpen] = useState<boolean>(false);
  const [resultInProgress, setResultInProgress] = useState<boolean>(false);
  const [resultTransaction, setResultTransaction] = useState<HeadTransaction>();

  const pollingRef = useRef<boolean>(false);
  useEffect(() => () => void (pollingRef.current = false), []);

  const waitForFinalStatus = useCallback(
    (transaction: HeadTransaction): Promise<HeadTransaction> => {
      if (!pollingRef.current || transaction.status !== 'PROCESSING') {
        return Promise.resolve(transaction);
      }
      return delay(STATUS_POLL_INTERVAL)
        .then(() => getTransaction({ id: transaction.id }))
        .then(({ data }) => waitForFinalStatus(data))
        .catch(() => waitForFinalStatus(transaction));
    },
    []
  );

  const createTransaction = (c: HeadTransaction) => {
    switch (c.operationType) {
      case 'SETTLEMENT':
        return createSettlementTransaction({ consentId: c.id });
      case 'ACCOUNT_TRANSFER':
        return createAccountTransferTransaction({ consentId: c.id });
      case 'DEBIT_ORDER':
        return createDebitOrderTransaction({ consentId: c.id });
      default:
        return null;
    }
  };

  const onCreateTransaction = () => {
    const promise = consent?.consent && createTransaction(consent.consent);
    if (!promise) return;

    setLoading(true);
    setResultTransaction(undefined);
    setResultInProgress(true);
    setResultOpen(true);
    pollingRef.current = true;

    promise
      .then(({ data: transaction }) => {
        return waitForFinalStatus(transaction);
      })
      .then((transaction) => {
        if (!pollingRef.current) return;
        setResultTransaction(transaction);
        setResultInProgress(false);
      })
      .catch((error) => {
        notify({ message: error?.body?.message || error.message, type: 'error' });
        onResultClose();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onResultClose = () => {
    pollingRef.current = false;
    setOpen(false);
    setResultOpen(false);
    onDone();
  };

  const onGoToTransaction = (transactionId: string) => {
    onResultClose();
    history.push(`/headTransactions/${transactionId}/show`);
  };

  const consentActive = !!consent;

  const errorMessage = (error: string) => {
    switch (error) {
      case 'NOT_SUFFICIENT_FUNDS':
        return 'Not sufficient funds!';
      default:
        return 'Transaction is not possible.';
    }
  };

  const hasSettlementPermission = permissions?.includes('settlement.runTransfer');
  const hasAccountTransferPermission = permissions?.includes('accountTransfer.runTransfer');
  const hasDebitOrderPermission = permissions?.includes('debitOrder.runTransfer');

  return (
    <Fragment>
      <Button
        label={t('transfer')}
        // className={cx({ [classes.warning]: warning, [classes.red]: red })}
        onClick={onClick}
      >
        <SwapHoriz />
      </Button>
      <Drawer anchor='bottom' open={open} onClose={handleClose}>
        <Container maxWidth='md' className={classes.container}>
          <Grid container spacing={7}>
            <Grid container item xs={12} alignItems='center' justify='space-between'>
              <Grid item xs>
                <H2 topSpacing={0} bottomSpacing={0} className={classes.title}>
                  {t('transferHeadline')}
                </H2>
                <Typography variant='subtitle1'>{t('transferSubtitle')}</Typography>
              </Grid>
              <Grid container item justify='flex-end' spacing={2} xs={4}>
                <Grid item>
                  <Button
                    label='ra.action.cancel'
                    onClick={handleClose}
                    className={classes.red}
                    size='large'
                  >
                    <SvgIcon>
                      <CloseCircleIcon />
                    </SvgIcon>
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    label={t('approve')}
                    color='primary'
                    size='large'
                    variant='contained'
                    disabled={!consent?.consent || loading}
                    onClick={onCreateTransaction}
                  >
                    {loading ? (
                      <CircularProgress size={18} thickness={2} />
                    ) : (
                      <SvgIcon>
                        <ArrowForwardCircleIcon />
                      </SvgIcon>
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Stepper nonLinear orientation='vertical'>
                <Step active expanded>
                  <StepLabel>{t('selectOperationType')}</StepLabel>
                  <StepContent>
                    <OperationTypeInput
                      label={t('operationType')}
                      value={operationType!}
                      onChange={(e) => {
                        setOperationType(e.target.value);
                        setConsent(undefined);
                        if (e.key === 'Enter') {
                          e.stopPropagation();
                        }
                      }}
                      style={{
                        width: 240,
                      }}
                      optionsFilter={(o) => {
                        return (
                          (hasSettlementPermission && o.value === 'SETTLEMENT') ||
                          (hasAccountTransferPermission && o.value === 'ACCOUNT_TRANSFER') ||
                          (hasDebitOrderPermission &&
                            (resource === 'individuals' || resource === 'businesses') &&
                            o.value === 'DEBIT_ORDER') // debit order is only allowed for individual/business profiles
                        );
                      }}
                      allowEmpty
                    />
                  </StepContent>
                </Step>
                <Step active={!!operationType} expanded={!!operationType}>
                  <StepLabel>{t('inputDetails')}</StepLabel>
                  <StepContent>
                    {operationType === 'ACCOUNT_TRANSFER' ? (
                      <AccountTransferForm
                        resource={resource}
                        profileId={profileId}
                        onConsent={onConsent}
                      />
                    ) : operationType === 'SETTLEMENT' ? (
                      <SettlementForm
                        resource={resource}
                        profileId={profileId}
                        onConsent={onConsent}
                      />
                    ) : operationType === 'DEBIT_ORDER' ? (
                      <DebitOrderForm
                        resource={resource}
                        profileId={profileId}
                        onConsent={onConsent}
                      />
                    ) : null}
                  </StepContent>
                </Step>
                <Step active={consentActive} expanded={consentActive}>
                  <StepLabel>{t('checkConsent')}</StepLabel>
                  <StepContent>
                    {consent?.consent ? (
                      <RunTransferActionConsent consent={consent.consent} />
                    ) : consent?.error ? (
                      <>{errorMessage(consent.error)}</>
                    ) : (
                      <>
                        <Skeleton style={{ width: 400, height: 20 }} />
                        <Skeleton style={{ width: 400, height: 20 }} />
                        <Skeleton style={{ width: 400, height: 20 }} />
                      </>
                    )}
                  </StepContent>
                </Step>
              </Stepper>
            </Grid>
          </Grid>
        </Container>
      </Drawer>
      <RunTransferActionResult
        open={resultOpen}
        inProgress={resultInProgress}
        transaction={resultTransaction}
        onClose={onResultClose}
        onGoToTransaction={onGoToTransaction}
      />
    </Fragment>
  );
};

const useStyles = makeStyles((theme) => ({
  container: {
    height: '100vh',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  title: {
    fontSize: '32px',
  },
  red: {
    color: theme.palette.error.main,
  },
  bottomSpacing: {
    paddingBottom: theme.spacing(2),
  },
  labelBottomSpacing: {
    paddingBottom: theme.spacing(1),
  },
}));

export default RunTransferAction;
